import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Layout, Card, Button, Space, Spin, message, Typography, Form, Input } from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { getAccessTokenApi } from 'api/auth';
import {
  getPdfTemplateByTypeApi,
  createPdfTemplateApi,
  updatePdfTemplateApi,
  getPdfTemplateByTypeAdminApi,
  createPdfTemplateAdminApi,
  updatePdfTemplateAdminApi,
} from 'api/pdfTemplate';
import SectionList from './SectionList';
import SectionEditor from './SectionEditor';
import TemplatePreview from './TemplatePreview';
import LogoUploader from './LogoUploader';
import TenantTextsPanel from './TenantTextsPanel';
import { findInvalidTableSections } from './utils/tableWidths';

const { Header, Content, Sider } = Layout;
const { Title } = Typography;

const ensureSectionIds = (sections = []) =>
  sections.map((s, i) => ({
    ...s,
    id: s.id || `section-${i}-${Date.now()}`,
  }));

// Si se pasa `tenantId` por prop, el editor edita plantillas cross-tenant
// vía el set de APIs `/admin/tenants/:tenantId/pdf-templates/*` (super-tenant).
// Si no, modo original: edita las del tenant del token.
function TemplateEditor({ documentType: propDocumentType, tenantId } = {}) {
  const params = useParams();
  const documentType = propDocumentType || params.documentType;
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null);

  const loadTemplate = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenApi();
      const result = tenantId
        ? await getPdfTemplateByTypeAdminApi(token, tenantId, documentType)
        : await getPdfTemplateByTypeApi(token, documentType);
      if (result?.status === 'client') {
        throw new Error(result.message);
      }
      setTemplate({
        ...result,
        sections: ensureSectionIds(result?.sections || []),
      });
    } catch (err) {
      message.error(`Error al cargar template: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [documentType, tenantId]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const invalidTableSections = template ? findInvalidTableSections(template.sections || []) : [];
  const hasInvalidTables = invalidTableSections.length > 0;

  const handleSave = async () => {
    if (hasInvalidTables) {
      message.error(
        `No se puede guardar: los anchos de columna no suman 100% en: ${invalidTableSections.join(
          ', '
        )}`
      );
      return;
    }
    setSaving(true);
    try {
      const token = await getAccessTokenApi();
      let result;
      if (tenantId) {
        result = template?.id
          ? await updatePdfTemplateAdminApi(token, tenantId, template.id, template)
          : await createPdfTemplateAdminApi(token, tenantId, template);
      } else {
        result = template?.id
          ? await updatePdfTemplateApi(token, template.id, template)
          : await createPdfTemplateApi(token, template);
      }
      if (result?.status === 'client') throw new Error(result.message);
      message.success('Plantilla guardada');
      setTemplate({
        ...result,
        sections: ensureSectionIds(result?.sections || []),
      });
    } catch (err) {
      message.error(`Error al guardar: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = (nextSections) => setTemplate({ ...template, sections: nextSections });

  const handleToggleVisible = (sectionId, visible) => {
    setTemplate({
      ...template,
      sections: template.sections.map((s) => (s.id === sectionId ? { ...s, visible } : s)),
    });
  };

  const handleSectionSave = (updatedSection) => {
    setTemplate({
      ...template,
      sections: template.sections.map((s) => (s.id === updatedSection.id ? updatedSection : s)),
    });
  };

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!template) {
    return <div style={{ padding: 48 }}>No se pudo cargar el template.</div>;
  }

  return (
    <Layout style={{ height: 'calc(100vh - 64px)', background: '#fff' }}>
      <Header
        style={{
          background: '#fff',
          borderBottom: '1px solid #e8e8e8',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Editor de plantilla — {documentType}
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadTemplate}>
            Recargar
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
            disabled={hasInvalidTables}
            title={
              hasInvalidTables
                ? `Los anchos deben sumar 100% en: ${invalidTableSections.join(', ')}`
                : ''
            }
          >
            Guardar
          </Button>
        </Space>
      </Header>
      <Layout>
        <Sider
          width={tenantId ? '40%' : '55%'}
          style={{ background: '#fafafa', padding: 16, overflow: 'auto' }}
        >
          <Card size="small" title="Metadatos" style={{ marginBottom: 16 }}>
            <Form layout="vertical">
              <Form.Item label="Nombre del documento">
                <Input
                  value={template.name}
                  onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                />
              </Form.Item>
              <Form.Item label="Logo">
                <LogoUploader
                  value={template.logo}
                  onChange={(logo) => setTemplate({ ...template, logo })}
                  tenantId={tenantId}
                  selfTenant={!tenantId}
                />
              </Form.Item>
            </Form>
          </Card>
          <Card size="small" title="Secciones (arrastra para reordenar)">
            <SectionList
              sections={template.sections}
              onReorder={handleReorder}
              onToggleVisible={handleToggleVisible}
              onEdit={(section) => setEditingSection(section)}
            />
          </Card>
        </Sider>
        <Content style={{ padding: 0, background: '#e8e8e8' }}>
          <TemplatePreview template={template} />
        </Content>
        {tenantId ? (
          <Sider
            width={'25%'}
            style={{
              background: '#fff',
              padding: 16,
              borderLeft: '1px solid #e8e8e8',
              overflow: 'auto',
            }}
          >
            <TenantTextsPanel tenantId={tenantId} documentType={documentType} />
          </Sider>
        ) : null}
      </Layout>
      <SectionEditor
        section={editingSection}
        documentType={documentType}
        tenantId={tenantId}
        open={!!editingSection}
        onClose={() => setEditingSection(null)}
        onSave={handleSectionSave}
      />
    </Layout>
  );
}

export default TemplateEditor;
